import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie, ShieldCheck, BarChart3, Megaphone, X, Settings2 } from "lucide-react";
import { useCart } from "../../context/CartContext";

const STORAGE_KEY = "leovera_cookie_consent_v1";

/** Public helper — read the current consent from anywhere in the app. */
export const getCookieConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const saveConsent = (choice) => {
  const payload = {
    necessary: true, // always required
    analytics: !!choice.analytics,
    marketing: !!choice.marketing,
    version: 1,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  // Broadcast so the app / analytics scripts can react without a reload
  window.dispatchEvent(new CustomEvent("leovera:cookie-consent", { detail: payload }));
  return payload;
};

const Toggle = ({ label, description, icon: Icon, checked, disabled, onChange, testid }) => (
  <label
    data-testid={testid}
    className={`flex items-start gap-3 p-4 rounded-sm border transition-colors ${
      disabled
        ? "bg-[#0A1121] border-white/5 opacity-80"
        : "bg-[#0A1121] border-white/10 hover:border-[#C0C0C0]/40 cursor-pointer"
    }`}
  >
    <span className="mt-0.5 shrink-0 text-[#C0C0C0]">
      <Icon className="w-4 h-4" strokeWidth={1.5} />
    </span>
    <span className="flex-1 min-w-0">
      <span className="flex items-center gap-2">
        <span className="font-[Outfit] text-white text-[13px] tracking-tight">{label}</span>
        {disabled && (
          <span className="text-[9px] tracking-[0.24em] uppercase text-emerald-300/80">Obbligatorio</span>
        )}
      </span>
      <span className="block mt-1 text-[11px] leading-relaxed text-[#94A3B8]">
        {description}
      </span>
    </span>
    <span className="shrink-0 pt-0.5">
      <input
        type="checkbox"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="w-4 h-4 accent-[#C0C0C0] cursor-pointer disabled:cursor-not-allowed"
      />
    </span>
  </label>
);

const CookieBanner = () => {
  const location = useLocation();
  const cart = useCart();
  const [open, setOpen] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [choice, setChoice] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const existing = getCookieConsent();
    if (!existing) setOpen(true);

    // Allow the user to re-open the banner via Footer link
    const reopen = () => {
      const cur = getCookieConsent() || { analytics: false, marketing: false };
      setChoice({ analytics: !!cur.analytics, marketing: !!cur.marketing });
      setShowCustomize(true);
      setOpen(true);
    };
    window.addEventListener("leovera:open-cookie-preferences", reopen);
    return () => window.removeEventListener("leovera:open-cookie-preferences", reopen);
  }, []);

  // Admin dashboard is an internal tool, not a public visitor surface —
  // the consent banner must never overlay it (e.g. Logistica panel).
  if (location.pathname.startsWith("/admin")) return null;
  if (!open) return null;
  // The cart drawer is a modal: the banner sat on top of its privacy checkbox
  // and "Vai alla cassa" button, and clicking the banner closed the drawer.
  // Step aside while the cart is open; it comes back when the drawer closes.
  if (cart?.open) return null;

  const acceptAll = () => {
    saveConsent({ analytics: true, marketing: true });
    setOpen(false);
  };
  const declineAll = () => {
    saveConsent({ analytics: false, marketing: false });
    setOpen(false);
  };
  const savePreferences = () => {
    saveConsent(choice);
    setOpen(false);
  };

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-live="polite"
      aria-label="Preferenze cookie e privacy"
      className="fixed inset-x-0 bottom-0 z-[70] px-3 sm:px-5 pb-3 sm:pb-5 pointer-events-none"
    >
      <div className="pointer-events-auto max-w-4xl mx-auto bg-[#060A14]/95 backdrop-blur-md border border-[#C0C0C0]/25 rounded-sm shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)]">
        <div className="p-5 sm:p-7">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-[#C0C0C0]/10 border border-[#C0C0C0]/25 flex items-center justify-center">
                <Cookie className="w-4 h-4 text-[#C0C0C0]" strokeWidth={1.5} />
              </span>
              <div>
                <span className="text-[10px] tracking-[0.32em] uppercase text-[#C0C0C0]">Privacy & Cookie</span>
                <h3 className="mt-1 font-[Outfit] text-white text-lg sm:text-xl tracking-tight font-light">
                  Rispettiamo la tua privacy
                </h3>
              </div>
            </div>
            <button
              type="button"
              onClick={declineAll}
              aria-label="Chiudi rifiutando i cookie non necessari"
              data-testid="cookie-banner-close"
              className="text-[#94A3B8] hover:text-white p-1.5"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <p className="mt-4 text-[13px] leading-relaxed text-[#CBD5E1]">
            Utilizziamo cookie tecnici necessari al funzionamento del sito e, previo tuo
            consenso, cookie di analisi e marketing per migliorare l'esperienza e proporti
            offerte pertinenti. Il sito include inoltre <strong className="text-[#E5E4E2] font-normal">Leo</strong>,
            un assistente virtuale basato su intelligenza artificiale che elabora i messaggi
            della chat per aiutarti nella scelta dei prodotti. Puoi accettare tutto, rifiutare
            i non necessari o personalizzare la tua scelta. Consulta la nostra{" "}
            <Link
              to="/cookie-policy"
              onClick={() => setOpen(false)}
              data-testid="cookie-banner-policy-link"
              className="underline underline-offset-2 text-[#E5E4E2] hover:text-white"
            >
              Cookie Policy
            </Link>{" "}
            e la{" "}
            <Link
              to="/privacy"
              onClick={() => setOpen(false)}
              data-testid="cookie-banner-privacy-link"
              className="underline underline-offset-2 text-[#E5E4E2] hover:text-white"
            >
              Privacy Policy
            </Link>
            .
          </p>

          {showCustomize && (
            <div className="mt-5 grid grid-cols-1 gap-2" data-testid="cookie-banner-customize-panel">
              <Toggle
                testid="cookie-toggle-necessary"
                icon={ShieldCheck}
                label="Cookie tecnici (necessari)"
                description="Indispensabili per il funzionamento del sito, l'autenticazione, il carrello e la sicurezza. Non possono essere disattivati."
                checked
                disabled
              />
              <Toggle
                testid="cookie-toggle-analytics"
                icon={BarChart3}
                label="Cookie di analisi"
                description="Ci aiutano a capire come viene utilizzato il sito in forma aggregata e anonima per migliorare i contenuti."
                checked={choice.analytics}
                onChange={(v) => setChoice((c) => ({ ...c, analytics: v }))}
              />
              <Toggle
                testid="cookie-toggle-marketing"
                icon={Megaphone}
                label="Cookie marketing"
                description="Permettono di mostrarti offerte, sconti e comunicazioni personalizzate su questo sito e su piattaforme partner."
                checked={choice.marketing}
                onChange={(v) => setChoice((c) => ({ ...c, marketing: v }))}
              />
            </div>
          )}

          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {!showCustomize ? (
                <button
                  type="button"
                  onClick={() => setShowCustomize(true)}
                  data-testid="cookie-banner-customize"
                  className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#E5E4E2] hover:text-white"
                >
                  <Settings2 className="w-3.5 h-3.5" />
                  Personalizza
                </button>
              ) : (
                <button
                  type="button"
                  onClick={savePreferences}
                  data-testid="cookie-banner-save"
                  className="inline-flex items-center gap-2 border border-[#C0C0C0]/40 text-[#E5E4E2] px-5 py-3 rounded-sm text-[11px] tracking-[0.28em] uppercase hover:bg-[#C0C0C0]/10"
                >
                  Salva preferenze
                </button>
              )}
            </div>

            <div className="flex flex-wrap gap-2 sm:justify-end">
              <button
                type="button"
                onClick={declineAll}
                data-testid="cookie-banner-decline"
                className="border border-white/15 text-[#E5E4E2] px-5 py-3 rounded-sm text-[11px] tracking-[0.28em] uppercase hover:bg-white/5"
              >
                Rifiuta non necessari
              </button>
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-banner-accept-all"
                className="bg-[#C0C0C0] text-[#060A14] px-6 py-3 rounded-sm text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-white"
              >
                Accetta tutto
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
